// Shared by the office Haul/Launch board (haulLaunch.js) and the techs app's
// haul/launch list (haulLaunchTech.js), so both build the same boat list, put it in
// the same order, and mark a boat hauled/launched with exactly the same writes.

import { ref, get, runTransaction } from 'firebase/database';
import { update, set, remove } from './trackedDb'; // durable offline writes
import { database, isFirebaseConnected } from './initFirebase';
import { idbCursorAll, getDB, updateTimestamps, generateId } from './utilities';

export const SLOTS = ['7:30a', '8:30a', '9:30a', '10:30a', '11:30a', '1:00p', '2:00p', '3:00p'];

// type -> which fields/category drive that side of the board. Mirrors the
// exact rules already used in spring.js (Spring Commissioning/Launch) and
// hauling.js (Winterizing/Haul) so this screen agrees with those.
export const TYPES = {
  Haul: { category: 'Winterizing', dateField: 'Haul_Date', timeField: 'Haul_Time', completedField: 'Date_Hauled', reasonField: 'Haul_Reason', tone: 'haul' },
  Launch: { category: 'Spring Commissioning', dateField: 'Launch_Date', timeField: 'Launch_Time', completedField: 'Date_Launched', reasonField: 'Launch_Reason', tone: 'launch' },
};

// Same description convention hauling.js/spring.js already rely on to find
// "the actual haul/launch operation" (and, for hauls, the linked pressure-
// wash op) within a work order's job rows. Matched primarily by OpCode (the
// stable identifier app.js stamps on these ops at creation — WS01/WS02/SP37),
// falling back to the description substring only for older rows created
// before OpCode was populated, so an edited/typo'd description can't silently
// break billing/status updates.
export const HAUL_OP_PHRASE = 'HAUL BOAT, STORAGE';
export const WASH_OP_PHRASE = 'PRESSURE WASH, STORAGE';
export const LAUNCH_OP_PHRASE = 'LAUNCH BOAT, STORAGE';
export const HAUL_OP_CODE = 'WS01';
export const WASH_OP_CODE = 'WS02';
export const LAUNCH_OP_CODE = 'SP37';

// What a lift photo shows. Sling location is the one the next crew looks for first.
export const LIFT_PHOTO_TAGS = ['Sling location', 'Blocking', 'Condition', 'Damage', 'Other'];

export function currentUserName() {
  try { return localStorage.getItem('currentUserName') || ''; } catch { return ''; }
}

export function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function formatISO(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// Ported from moveToNextMonday in spring.js/hauling.js — a boat requested for
// a Saturday/Sunday rolls forward onto the following Monday.
export function moveToNextMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  if (day === 6) d.setDate(d.getDate() + 2);
  else if (day === 0) d.setDate(d.getDate() + 1);
  return d;
}

export function mondayOf(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function findBoatRecord(customersById, job) {
  const customer = customersById.get(job.Customer_ID);
  if (!customer || !customer.Boats) return null;
  if (!job.Boat_ID) return null;
  return Object.values(customer.Boats).find(b => b.Boat_ID === job.Boat_ID) || null;
}

// A Closed/Declined work order is dead — it shouldn't keep flagging a boat as
// needing a haul/launch just because nobody went back and stamped
// Date_Hauled/Date_Launched on the boat record when it closed out years ago.
const DEAD_STATUSES = ['Closed', 'Declined'];

// Groups job rows by (Customer_ID, Boat_ID, type) rather than by Work_Order.
// The board's dates/completion flags all live on the boat record, not the
// job — so if a boat has two separate work orders both tagged with the same
// category (e.g. a haul WO and a separate shrinkwrap WO), grouping by
// Work_Order produces two cards for the same boat pointing at the identical
// boat-level date, which shows up as a duplicate. Grouping by boat+type
// collapses those back into one entry. Only an *open* work order counts — a
// historical job from a past season that's already Closed/Declined doesn't
// keep a boat on the board forever.
export function groupByBoatAndType(jobs) {
  // Pass 1: find the "anchor" row for each boat+type — the operation whose
  // own Category is Winterizing/Spring Commissioning. That tells us which
  // Work_Order this haul/launch actually belongs to.
  const workOrderToAnchor = {}; // Work_Order -> { key, typeName }
  jobs.forEach(j => {
    const typeName = Object.keys(TYPES).find(t => TYPES[t].category === j.Category);
    if (!typeName) return;
    if (j.Customer_ID === undefined || j.Boat_ID === undefined) return;
    if (DEAD_STATUSES.includes(j.Status)) return;
    const key = `${j.Customer_ID}_${j.Boat_ID}_${typeName}`;
    workOrderToAnchor[j.Work_Order] = { key, typeName };
  });

  // Pass 2: pull in every operation on that same work order, regardless of
  // its own Category — e.g. a "BOTTOM PAINT, STORAGE" prep operation usually
  // has a different Category than the haul/launch operation itself, but it
  // still needs to be on this boat's job list so rescheduling can reach it.
  const groups = {};
  jobs.forEach(j => {
    const anchor = workOrderToAnchor[j.Work_Order];
    if (!anchor) return;
    if (!groups[anchor.key]) groups[anchor.key] = { typeName: anchor.typeName, ops: [] };
    groups[anchor.key].ops.push(j);
  });
  return groups;
}

// A dependency op counts as "done" once it's reached the same terminal
// statuses the rest of the app treats as finished (mirrors wo-ops-
// aggregation.js's DONE_STATUSES). Declined ops are excluded from blocking
// entirely, separately — a declined op is off the work order, not still
// outstanding on it (same convention as aggregateWorkOrder()'s activeOps).
const GATE_DONE_STATUSES = ['Ready to Bill', 'Complete', 'Closed'];

// Loads the set of OpCodes flagged Dependency-truthy in the catalog (Settings
// > Op Codes), from the same IDB store ('opCodeData') the admin tab reads.
export async function loadDependencyOpCodes() {
  try {
    const codes = await idbCursorAll('opCodeData');
    return new Set(codes.filter(c => Number(c.Dependency) > 0).map(c => String(c.OpCode)));
  } catch (error) {
    console.error('[haulLaunch] failed to load op-code dependency flags', error);
    return new Set();
  }
}

// Ops on a boat's work order that are catalog-flagged as a dependency and
// haven't reached a done status yet — i.e. what's still holding up Ready to
// Haul/Ready to Launch. `primaryCode`/`washCode` are excluded defensively (an
// op can't block its own haul/launch action even if someone flags it in the
// catalog by mistake).
export function blockingOpsFor(ops, dependencyOpCodes, primaryCode, washCode) {
  return ops.filter(o =>
    dependencyOpCodes.has(String(o.OpCode)) &&
    o.OpCode !== primaryCode && o.OpCode !== washCode &&
    (o.Status || '').toString() !== 'Declined' &&
    !GATE_DONE_STATUSES.includes((o.Status || '').toString())
  );
}

// Builds the list of boats relevant to the board: one entry per
// boat+haul-or-launch, with dates/times read from the persistent boat record
// (job-level copies go stale once a work order is closed out).
// Spring tie-in: a boat launching onto a mooring needs that mooring in the water.
// The mooring is the tag whose Current_Boat_ID is this boat; it's "in" once it was
// set (Placement_Date) this season and not hauled since.
export function mooringForBoat(boatId, mooringTags) {
  if (!boatId || !mooringTags || !mooringTags.length) return null;
  const tag = mooringTags.find(t => t && t.Current_Boat_ID === boatId && ['RENTAL', 'CUSTOMER'].includes(String(t.Type || '').toUpperCase()));
  if (!tag) return null;
  const year = String(new Date().getFullYear());
  const placed = String(tag.Placement_Date || '').startsWith(year) && !(tag.Haul_Date && tag.Haul_Date > tag.Placement_Date);
  return { number: tag.Current_Mooring_Number || '', tagNumber: tag.Tag_Number || '', placed };
}

// A phone that only runs the techs app has no local copy of the tags, so each list
// redraw asked the server for all of them again: the answer is kept for a couple of
// minutes (and dropped when this device saves a tag).
const TAGS_KEEP_MS = 2 * 60000;
let serverTags = null; // { at, promise }

function serverMooringTags() {
  if (serverTags && Date.now() - serverTags.at < TAGS_KEEP_MS) return serverTags.promise;
  const promise = get(ref(database, 'mooringDatabase/tags'))
    .then(snap => Object.entries(snap.val() || {}).filter(([, t]) => t && typeof t === 'object').map(([id, t]) => ({ id, ...t })));
  serverTags = { at: Date.now(), promise };
  promise.catch(() => { serverTags = null; });
  return promise;
}

// Mooring tags from the local cache, or the server when this device has none yet.
export async function loadMooringTags() {
  try {
    const all = await idbCursorAll('mooringDatabase');
    if (all.length) return all;
  } catch (error) {
    console.error('[haulLaunch] mooring tags from cache failed', error);
  }
  if (!isFirebaseConnected()) return [];
  try {
    return await serverMooringTags();
  } catch (error) {
    console.error('[haulLaunch] mooring tags from server failed', error);
    return [];
  }
}

export function buildBoats(jobs, customersById, dependencyOpCodes, mooringTags = []) {
  const groups = groupByBoatAndType(jobs);
  const currentYear = String(new Date().getFullYear());
  const boats = [];

  Object.keys(groups).forEach(key => {
    const { typeName, ops } = groups[key];
    const rep = ops[0];

    const def = TYPES[typeName];
    const boatRecord = findBoatRecord(customersById, rep);
    if (!boatRecord) return;

    const plannedDate = boatRecord[def.dateField];
    if (!plannedDate) return;
    if (plannedDate.split('-')[0] !== currentYear) return; // not this season's haul/launch cycle

    const completed = boatRecord[def.completedField];
    if (completed && completed.split('-')[0] === currentYear) return; // already done this season

    const shiftedDate = moveToNextMonday(parseLocalDate(plannedDate));
    const boatWeekStart = mondayOf(shiftedDate);

    const primaryCode = typeName === 'Haul' ? HAUL_OP_CODE : LAUNCH_OP_CODE;
    const blocking = blockingOpsFor(ops, dependencyOpCodes || new Set(), primaryCode, WASH_OP_CODE);

    boats.push({
      key,
      customerId: rep.Customer_ID,
      boatId: rep.Boat_ID,
      workOrder: rep.Work_Order,
      jobs: ops.filter(o => o.id).map(o => ({ id: o.id, shortDesc: o.Short_Description_DM || '', status: o.Status || '', opCode: o.OpCode || '' })),
      blockingOps: blocking.map(o => o.Short_Description_DM || o.Short_Description || o.Description || String(o.OpCode || '')),
      // Each blocking op's Scheduler day, for the "booked after the move" check (timingIssuesFor).
      blockingJobs: blockingJobsOf(blocking),
      ready: blocking.length === 0,
      type: typeName,
      tone: def.tone,
      timeField: def.timeField,
      dateField: def.dateField,
      completedField: def.completedField,
      boat: rep.Boat_Name,
      customer: rep.Customer_Name,
      loa: boatRecord.Boat_Length || '',
      tide: boatRecord.Boat_Type === 'Sailboat',
      requested: plannedDate,
      day: formatISO(shiftedDate), // the day it's actually on the list (a weekend request rolls to Monday)
      time: boatRecord[def.timeField] || null,
      weekStart: boatWeekStart,
      weekday: shiftedDate.getDay(), // 1=Mon..5=Fri
      reasonField: def.reasonField,
      reason: boatRecord[def.reasonField] || 'Seasonal',
      locationKind: boatRecord.Boat_Location_Status || null,
      locationDetail: boatRecord.Boat_Location_Detail || '',
      mooring: typeName === 'Launch' ? mooringForBoat(rep.Boat_ID, mooringTags) : null,
    });
  });

  return boats;
}

export function blockingJobsOf(blocking) {
  return blocking.map(o => ({
    id: o.id || '',
    desc: o.Short_Description_DM || o.Short_Description || o.Description || String(o.OpCode || ''),
    scheduleDay: /^\d{4}-\d{2}-\d{2}/.test(String(o.Schedule_Date || '')) ? String(o.Schedule_Date).slice(0, 10) : null,
  }));
}

// Blocking ops that won't be done before the move, by their Scheduler day: not booked at all,
// or booked after the move's day. A past, unfinished booking counts as today (the Scheduler
// rolls it forward). Boats whose move day has already passed are left out: they're overdue,
// which the board already shows. Returns [{ id, desc, problem }] with problem 'unscheduled' | 'late'.
export function timingIssuesFor(boat, today = formatISO(new Date())) {
  if (!boat || !boat.blockingJobs || !boat.blockingJobs.length || boat.day < today) return [];
  return boat.blockingJobs
    .map(j => {
      if (!j.scheduleDay) return { ...j, problem: 'unscheduled' };
      const effective = j.scheduleDay < today ? today : j.scheduleDay;
      // Same day is normal (e.g. winterizing an inboard in the water the morning of the haul).
      return effective > boat.day ? { ...j, problem: 'late', effectiveDay: effective } : null;
    })
    .filter(Boolean);
}

// ---------- the day's list: order ----------

// Minutes since midnight for a slot ('7:30a', '1:00p') or a 24h time ('13:00').
// Boats with no time sort after every timed one.
export function timeSortKey(time) {
  const m = /^\s*(\d{1,2}):(\d{2})\s*([ap])?/i.exec(time || '');
  if (!m) return Infinity;
  let h = Number(m[1]);
  const p = (m[3] || '').toLowerCase();
  if (p === 'p' && h < 12) h += 12;
  if (p === 'a' && h === 12) h = 0;
  return h * 60 + Number(m[2]);
}

function byTime(a, b) {
  return (timeSortKey(a.time) - timeSortKey(b.time)) || String(a.boat || '').localeCompare(String(b.boat || ''));
}

// The order the crew works the day in: the office's custom order when one was
// saved (boats added to the day afterwards fall in behind it, by time), otherwise
// by time slot.
export function orderBoats(boats, order) {
  const list = boats.slice();
  if (!Array.isArray(order) || !order.length) return list.sort(byTime);
  const pos = new Map(order.map((key, i) => [key, i]));
  return list.sort((a, b) => {
    const pa = pos.has(a.key) ? pos.get(a.key) : Infinity;
    const pb = pos.has(b.key) ? pos.get(b.key) : Infinity;
    if (pa !== pb) return pa - pb;
    return byTime(a, b);
  });
}

// The boats on one day's list, plus (for a day that is today or earlier) the ones
// still not done from earlier days — overdue boats stay relevant until they're moved.
export function boatsForDay(boats, dateString, todayString) {
  const list = boats.filter(b => b.day === dateString);
  const overdue = dateString <= todayString ? boats.filter(b => b.day < dateString) : [];
  return { list, overdue };
}

// ---------- duty days + custom order (/haulLaunchOps/duty/{date}) ----------
//   techs: { [uid]: { name, by, at } }   who works the list that day
//   order: [boatKey, ...]                 the office's custom order, if any

export async function loadDutyDay(dateString) {
  const snap = await get(ref(database, `haulLaunchOps/duty/${dateString}`));
  return snap.val() || {};
}

export async function loadAllDuty() {
  const snap = await get(ref(database, 'haulLaunchOps/duty'));
  return snap.val() || {};
}

export function setDutyTech(dateString, tech, on, by) {
  const entry = on ? { name: tech.name, by: by || '', at: new Date().toISOString() } : null;
  return settle(update(ref(database, `haulLaunchOps/duty/${dateString}/techs`), { [tech.id]: entry }));
}

// keys = null goes back to time order.
export function saveDayOrder(dateString, keys, by) {
  return settle(update(ref(database, `haulLaunchOps/duty/${dateString}`), {
    order: keys && keys.length ? keys : null,
    orderBy: keys && keys.length ? (by || '') : null,
    orderAt: keys && keys.length ? new Date().toISOString() : null,
  }));
}

// ---------- issues (/haulLaunchOps/issues/{id}) ----------

export async function loadIssues() {
  const snap = await get(ref(database, 'haulLaunchOps/issues'));
  const val = snap.val() || {};
  return Object.entries(val).map(([id, v]) => ({ id, ...v }));
}

export function reportIssue(boat, note, by) {
  const id = generateId();
  const record = {
    date: formatISO(new Date()),
    boatKey: boat.key,
    customerId: boat.customerId,
    boatId: boat.boatId,
    boat: boat.boat || '',
    customer: boat.customer || '',
    type: boat.type,
    workOrder: boat.workOrder || '',
    note,
    by: by || '',
    at: new Date().toISOString(),
    resolved: false,
  };
  return settle(set(ref(database, `haulLaunchOps/issues/${id}`), record)).then(() => ({ id, ...record }));
}

export function resolveIssue(id, by) {
  return settle(update(ref(database, `haulLaunchOps/issues/${id}`), { resolved: true, resolvedBy: by || '', resolvedAt: new Date().toISOString() }));
}

// ---------- writes ----------

// While disconnected the SDK's promise only settles once the server acknowledges,
// which may be hours away on the dock. trackedDb keeps the write safe on the device,
// so offline the caller carries on without waiting for it.
function settle(promise) {
  if (isFirebaseConnected()) return promise;
  promise.catch(err => console.error('[haulLaunch] queued write failed', err));
  return Promise.resolve();
}

// Generic dual-write helper for stores keyed the same way customerData/jobData
// are (RTDB key mirrored as the IDB record's own `id`).
export async function patchStoreRecord(storeName, id, fields) {
  const db = await getDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const getRequest = store.get(id);
    getRequest.onsuccess = () => {
      const record = getRequest.result;
      if (record) {
        Object.assign(record, fields);
        const putRequest = store.put(record);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve();
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

// Write RTDB then the local customerData cache, so this session's offline-first
// state and any other tab reading the same IDB store stay consistent. A field
// value of null removes it.
export async function patchBoatFields(boat, fields) {
  await settle(update(ref(database, `/customers/${boat.customerId}/Boats/${boat.boatId}`), fields));

  const db = await getDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction('customerData', 'readwrite');
    const store = tx.objectStore('customerData');
    const getRequest = store.get(boat.customerId);
    getRequest.onsuccess = () => {
      const customer = getRequest.result;
      const record = customer && customer.Boats
        ? (customer.Boats[boat.boatId] || Object.values(customer.Boats).find(b => b.Boat_ID === boat.boatId)) : null;
      if (record) {
        Object.entries(fields).forEach(([k, v]) => { if (v === null) delete record[k]; else record[k] = v; });
        const putRequest = store.put(customer);
        putRequest.onsuccess = () => { updateTimestamps('customerData', 'customers'); resolve(); };
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve();
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

export async function patchJobFields(jobId, fields) {
  await settle(update(ref(database, `/data/${jobId}`), fields));
  await patchStoreRecord('jobData', jobId, fields);
}

async function patchMooringTag(tagId, fields) {
  serverTags = null;
  await settle(update(ref(database, `/mooringDatabase/tags/${tagId}`), fields));
  await patchStoreRecord('mooringDatabase', tagId, fields);
  updateTimestamps('mooringDatabase', 'mooringDatabase');
}

async function patchRental(rentalId, fields) {
  await settle(update(ref(database, `/mooringDatabase/rentals/${rentalId}`), fields));
  await patchStoreRecord('rentalDatabase', rentalId, fields);
  updateTimestamps('rentalDatabase', 'rentalDatabase');
}

// The mooring tag and this year's rental for a boat, from the local cache. A phone
// that has only ever opened the techs app may not have these stores filled, so when
// the cache has nothing at all and there's a connection, ask the server.
async function findMooringTagForBoat(boatId) {
  try {
    const all = await idbCursorAll('mooringDatabase');
    if (all.length) return all.find(t => t.Current_Boat_ID === boatId) || null;
  } catch (error) {
    console.error('[haulLaunch] mooring tag lookup failed', error);
  }
  if (!isFirebaseConnected()) return null;
  try {
    serverTags = null; // about to change this boat's tag: read it fresh
    return (await serverMooringTags()).find(t => t.Current_Boat_ID === boatId) || null;
  } catch (error) {
    console.error('[haulLaunch] mooring tag lookup failed', error);
    return null;
  }
}

async function findCurrentYearRental(tagId) {
  const year = String(new Date().getFullYear());
  const isThisYear = r => r && r.Tag_ID === tagId && r.Date_of_Request && r.Date_of_Request.split('-')[0] === year;
  try {
    const all = await idbCursorAll('rentalDatabase');
    if (all.length) return all.find(isThisYear) || null;
  } catch (error) {
    console.error('[haulLaunch] rental lookup failed', error);
  }
  if (!isFirebaseConnected()) return null;
  try {
    const snap = await get(ref(database, 'mooringDatabase/rentals'));
    const hit = Object.entries(snap.val() || {}).find(([, r]) => isThisYear(r));
    return hit ? { id: hit[0], ...hit[1] } : null;
  } catch (error) {
    console.error('[haulLaunch] rental lookup failed', error);
    return null;
  }
}

// J1 Winter Plan (maps.html/maps.js): a planned boat is a maps/boats record
// with status:'planned' — a dashed ghost until it "arrives". Completing the
// Hauling op is the one real signal that a boat is physically in the yard, so
// this is where that ghost turns solid, not a separate manual step staff have
// to remember on the map. No-ops for boats with no map record (most
// winterizing customers don't have one yet) or that already arrived. Needs a
// connection to read the record, so offline it's left for the map to catch up.
async function clearMapsGhostIfPlanned(boatId) {
  if (!boatId || !isFirebaseConnected()) return;
  try {
    const snap = await get(ref(database, `maps/boats/${boatId}/status`));
    if (snap.exists() && snap.val() === 'planned') {
      await update(ref(database, `maps/boats/${boatId}`), { status: '' });
    }
  } catch (error) {
    console.error('[haulLaunch] failed to clear planned status on maps ghost', error);
  }
}

function findOpByCode(boat, opCode, phrase) {
  return boat.jobs.find(j => j.opCode === opCode) || boat.jobs.find(j => j.shortDesc.toUpperCase().includes(phrase)) || null;
}

// Marks a boat hauled/launched — every write the move implies, in one place:
// the boat's completed date, the haul/launch op (and a haul's pressure-wash op)
// to Ready to Bill, the yard-map ghost, the mooring tag + this year's rental
// (hauls), and a permanent history record.
//   opts.note      goes on the history record
//   opts.by        who did it
//   opts.via       'office' | 'techs'
//   opts.location  { kind, detail } — where the boat went, saved on the boat record
// Returns { before, historyRef } for undoMove(). On failure it rolls back what it
// had already written and rethrows.
export async function completeMove(boat, opts = {}) {
  const today = formatISO(new Date());
  const isHaul = boat.type === 'Haul';
  const before = {};
  let historyRef = null;

  // Collaboration: two people marking the same boat at once (office + crew, or two phones) would
  // each write a history record and re-run the op side effects. Claim the boat's completed date
  // first; whoever loses is told it's done. (Offline there's no claim; the durable writes below
  // still go through when the connection is back.)
  before.boatPrev = boat[boat.completedField] || ''; // last season's date, put back on undo
  if (isFirebaseConnected()) {
    const claim = await runTransaction(ref(database, `customers/${boat.customerId}/Boats/${boat.boatId}/${boat.completedField}`), cur => {
      if (cur !== null) before.boatPrev = cur; // the server's (the first, cache-less run sees null)
      return cur === today ? undefined : today;
    });
    if (!claim.committed) {
      throw Object.assign(new Error(`${boat.boat} was already marked ${isHaul ? 'hauled' : 'launched'} today`), { alreadyDone: true });
    }
    before.boatDone = true;
  }

  try {
    const boatFields = { [boat.completedField]: today };
    if (opts.location && opts.location.kind) {
      before.location = { kind: boat.locationKind || '', detail: boat.locationDetail || '' };
      boatFields.Boat_Location_Status = opts.location.kind;
      boatFields.Boat_Location_Detail = opts.location.detail || '';
    }
    await patchBoatFields(boat, boatFields);
    before.boatDone = true;
    if (isHaul) await clearMapsGhostIfPlanned(boat.boatId);

    const primaryOp = findOpByCode(boat, isHaul ? HAUL_OP_CODE : LAUNCH_OP_CODE, isHaul ? HAUL_OP_PHRASE : LAUNCH_OP_PHRASE);
    if (primaryOp) {
      before.primaryOpId = primaryOp.id;
      before.primaryOpPrevStatus = primaryOp.status;
      await patchJobFields(primaryOp.id, { Status: 'Ready to Bill' });
    }

    if (isHaul) {
      const washOp = findOpByCode(boat, WASH_OP_CODE, WASH_OP_PHRASE);
      if (washOp) {
        before.washOpId = washOp.id;
        before.washOpPrevStatus = washOp.status;
        // Same status as the primary op — this is what actually fires
        // app.js's submitOpen() (shrinkwrap/pesticide checks, clearing
        // Operation_Dependency), which only listens for 'Ready to Bill'.
        await patchJobFields(washOp.id, { Status: 'Ready to Bill' });
      }

      // Rental moorings come out once the boat is hauled. A customer's mooring only
      // joins the haul list if their work order has a HAUL MOORING op, which
      // mooringOpsShared.haulBuckets() checks itself, so it isn't flagged here.
      const tag = await findMooringTagForBoat(boat.boatId);
      if (tag && (tag.Type || '').toUpperCase() === 'RENTAL') {
        before.tagId = tag.id;
        before.tagPrevReadyToHaul = !!tag.Ready_to_Haul;
        await patchMooringTag(tag.id, { Ready_to_Haul: true });

        const rental = await findCurrentYearRental(tag.id);
        if (rental) {
          before.rentalId = rental.id;
          before.rentalPrevReadyToHaul = !!rental.Ready_to_Haul;
          before.rentalPrevHaulDate = rental.Mooring_Haul_Date || '';
          await patchRental(rental.id, { Ready_to_Haul: true, Mooring_Haul_Date: today });
        }
      }
    }
    if (before.primaryOpId || before.washOpId) updateTimestamps('jobData', 'data');

    historyRef = await writeHistoryRecord(boat, opts);
    return { before, historyRef };
  } catch (error) {
    try {
      await undoMove(boat, before, historyRef);
    } catch (rollbackError) {
      console.error('[haulLaunch] rollback after failed completion also failed', rollbackError);
    }
    throw error;
  }
}

// Reverses completeMove(), using only what it recorded in `before` — so it also
// serves as the best-effort compensation when a later step of a completion threw.
export async function undoMove(boat, before, historyRef) {
  const boatFields = {};
  if (before.boatDone) boatFields[boat.completedField] = before.boatPrev ?? '';
  if (before.location) {
    boatFields.Boat_Location_Status = before.location.kind;
    boatFields.Boat_Location_Detail = before.location.detail;
  }
  if (Object.keys(boatFields).length) await patchBoatFields(boat, boatFields);
  if (before.primaryOpId) await patchJobFields(before.primaryOpId, { Status: before.primaryOpPrevStatus });
  if (before.washOpId) await patchJobFields(before.washOpId, { Status: before.washOpPrevStatus });
  if (before.primaryOpId || before.washOpId) updateTimestamps('jobData', 'data');
  if (before.tagId) await patchMooringTag(before.tagId, { Ready_to_Haul: before.tagPrevReadyToHaul });
  if (before.rentalId) await patchRental(before.rentalId, { Ready_to_Haul: before.rentalPrevReadyToHaul, Mooring_Haul_Date: before.rentalPrevHaulDate });
  if (historyRef) await settle(remove(ref(database, `/${historyRef.collection}/${historyRef.year}/${historyRef.recordId}`)));
}

// Permanent archive — one record per completed move, independent of the boat
// record's one-year Date_Hauled/Date_Launched field.
async function writeHistoryRecord(boat, opts) {
  const collection = boat.type === 'Haul' ? 'Haul_History' : 'Launch_History';
  const year = new Date().getFullYear();
  const recordId = generateId();
  const location = opts.location && opts.location.kind ? opts.location : { kind: boat.locationKind || '', detail: boat.locationDetail || '' };
  const record = {
    date: formatISO(new Date()),
    boat: boat.boat,
    customer: boat.customer,
    loa: boat.loa,
    workOrder: boat.workOrder || '',
    reason: boat.reason,
    locationKind: location.kind || '',
    locationDetail: location.detail || '',
    note: opts.note || '',
    completedAt: new Date().toISOString(),
    completedBy: opts.by || '',
    via: opts.via || 'office',
  };
  await settle(set(ref(database, `/${collection}/${year}/${recordId}`), record));
  return { collection, year, recordId };
}

// ---------- lift notes + photos (on the boat record, so they follow the boat) ----------
//   Boats/{id}/Lift_Notes            free text: sling marks, blocking, what to watch for
//   Boats/{id}/Lift_Photos/{key}     { path, tag, note, move, by, at }

export function liftPhotoPath(boat, stamp) {
  return `boat_photos/${boat.customerId}/${boat.boatId}/${stamp}.jpg`;
}

export function liftPhotoRecord(path, { tag, note, move, by }) {
  return { path, tag: tag || 'Other', note: note || '', move: move || '', by: by || '', at: new Date().toISOString() };
}

// Keyed by the capture timestamp in the path so a replayed write overwrites the
// same record instead of adding a duplicate.
export function liftPhotoKey(path) {
  return path.split('/').pop().replace(/\.jpg$/, '');
}

export function saveLiftPhoto(boat, record) {
  return patchBoatPhoto(boat, liftPhotoKey(record.path), record);
}

export function removeLiftPhoto(boat, key) {
  return patchBoatPhoto(boat, key, null);
}

async function patchBoatPhoto(boat, key, record) {
  await settle(update(ref(database, `/customers/${boat.customerId}/Boats/${boat.boatId}/Lift_Photos`), { [key]: record }));
  const db = await getDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction('customerData', 'readwrite');
    const store = tx.objectStore('customerData');
    const getRequest = store.get(boat.customerId);
    getRequest.onsuccess = () => {
      const customer = getRequest.result;
      const b = customer && customer.Boats
        ? (customer.Boats[boat.boatId] || Object.values(customer.Boats).find(x => x.Boat_ID === boat.boatId)) : null;
      if (b) {
        b.Lift_Photos = b.Lift_Photos || {};
        if (record) b.Lift_Photos[key] = record; else delete b.Lift_Photos[key];
        const putRequest = store.put(customer);
        putRequest.onsuccess = () => { updateTimestamps('customerData', 'customers'); resolve(); };
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve();
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

// Newest first; sling-location photos ahead of the rest, since that's what the
// crew needs with the boat in the slings.
export function sortedLiftPhotos(boatRecord) {
  return Object.entries((boatRecord && boatRecord.Lift_Photos) || {})
    .filter(([, p]) => p && p.path)
    .map(([key, p]) => ({ key, ...p }))
    .sort((a, b) => ((b.tag === 'Sling location') - (a.tag === 'Sling location')) || String(b.at || '').localeCompare(String(a.at || '')));
}
